import type {
  AIProvider,
  GeminiThinkingLevel,
  ModelKey,
} from '../../../config/ai-models'
import { MODELS } from '../../../config/ai-models'

/**
 * Model configuration for a recommendation request.
 * `type` is the provider (for API branching); `model` is the exact API model ID.
 */
export interface ModelConfig {
  type: AIProvider
  model: string
}

/**
 * Centralized configuration for the recommendation service.
 */
export interface RecommendationConfig {
  model: ModelConfig

  limits: {
    /** Maximum tokens for AI processing */
    maxTokens: number
    /** Maximum candidates to fetch from search */
    searchLimit: number
    /** Maximum candidates to send to AI for processing */
    processingLimit: number
    /** Maximum final recommendations to return */
    finalOutputLimit: number
    /** Maximum documents to select from complete dataset */
    maxDocumentSelection: number
  }

  parameters: {
    /**
     * Temperature for AI processing (lower = more consistent). Only reaches
     * providers that still honor it — llm.service drops it for Google
     * (deprecated) and for Claude models flagged `supportsSamplingParams:
     * false`.
     */
    temperature: number
    /**
     * Gemini 3.x thinking level (minimal | low | medium | high). Gemini runs
     * dynamic thinking by default (~1k thought tokens + several seconds of
     * latency on a rerank call); "minimal" eliminates it. Only applied for
     * Google models; ignored by other providers.
     */
    geminiThinking?: GeminiThinkingLevel
  }

  paths: {
    promptsDirectory: string
    gifSchema: string
  }

  defaults: {
    explicitRatings: string[]
    maxDuration?: number
  }
}

const DEFAULT_LIMITS: RecommendationConfig['limits'] = {
  maxTokens: 6000,
  searchLimit: 50,
  processingLimit: 50,
  finalOutputLimit: 3,
  maxDocumentSelection: 40,
}

const DEFAULT_PATHS: RecommendationConfig['paths'] = {
  promptsDirectory: 'prompts',
  gifSchema: 'gif-schema.txt',
}

const DEFAULT_DEFAULTS: RecommendationConfig['defaults'] = {
  explicitRatings: ['G', 'PG'],
  maxDuration: undefined,
}

/**
 * Pick a model + tune limits/temperature. Add a new entry to swap models without
 * editing the call sites — `buildRecommendationConfig('NAME')` is all you need.
 */
export interface RecommendationPreset {
  modelKey: ModelKey
  limits?: Partial<RecommendationConfig['limits']>
  /** See RecommendationConfig.parameters.temperature — ignored by Google and Claude 4.7+. */
  temperature: number
  /**
   * Gemini 3.x thinking level. Only meaningful for Google presets; other
   * providers ignore it. Omit to leave Gemini on its default dynamic thinking.
   */
  geminiThinking?: GeminiThinkingLevel
}

export const RECOMMENDATION_PRESETS = {
  // Flipped from GEMINI_3_5_FLASH on 2026-10-07 (eval
  // results/2026-10-07T17-38-25Z): on the 7 fixtures both completed, 3.8
  // Flash at "low" judged 2.63 / 2 traps / 9.3s / $0.031 per run vs 3.5
  // Flash at "minimal" 2.51 / 3 traps / 28.8s / $0.063, and 3.5 Flash was
  // returning 503 "high demand" on 5 of 12 runs. 3.8 has no "minimal"
  // level, so "low" is the floor; the cap leaves headroom for its thought
  // tokens. Earlier flip (2026-07-16, Haiku → 3.5 Flash) is recorded in
  // docs/architecture/recommendations-roadmap.md.
  PRODUCTION: {
    modelKey: 'GEMINI_3_8_FLASH',
    limits: { maxTokens: 16000 },
    temperature: 0.15,
    geminiThinking: 'low',
  },
  HIGH_QUALITY: {
    modelKey: 'CLAUDE_OPUS_4_8',
    limits: { maxTokens: 8000 },
    temperature: 0.2,
  },
  // Opus 5.5 / Sonnet 5.5 always think, and max_tokens covers thinking +
  // response, hence the larger caps (same reasoning as CLAUDE_SONNET_5).
  CLAUDE_OPUS_5_5: {
    modelKey: 'CLAUDE_OPUS_5_5',
    limits: { maxTokens: 16000 },
    temperature: 0.2,
  },
  CLAUDE_SONNET_5_5: {
    modelKey: 'CLAUDE_SONNET_5_5',
    limits: { maxTokens: 16000 },
    temperature: 0.2,
  },
  CLAUDE_SONNET_4_6: {
    modelKey: 'CLAUDE_SONNET_4_6',
    limits: { maxTokens: 8000 },
    temperature: 0.2,
  },
  // Sonnet 5 runs adaptive thinking by default and max_tokens covers
  // thinking + response, hence the larger cap. Temperature is stripped by
  // the llm.service sampling-param guard.
  CLAUDE_SONNET_5: {
    modelKey: 'CLAUDE_SONNET_5',
    limits: { maxTokens: 16000 },
    temperature: 0.2,
  },
  // Production config until 2026-10-07, kept for A/B runs. "minimal"
  // thinking eliminates Gemini's default dynamic reasoning (~1k thought
  // tokens + several seconds of latency on a rerank call). With thinking
  // off the rerank output is well under 2k tokens, so maxTokens no longer
  // needs the 40k headroom that thinking required.
  GEMINI_3_5_FLASH: {
    modelKey: 'GEMINI_3_5_FLASH',
    limits: { maxTokens: 8000 },
    temperature: 0.15,
    geminiThinking: 'minimal',
  },
  // 3.8 Flash's floor is "low" thinking (no "minimal"), so expect some
  // thought tokens per rerank; the cap leaves headroom for them.
  GEMINI_3_8_FLASH: {
    modelKey: 'GEMINI_3_8_FLASH',
    limits: { maxTokens: 16000 },
    temperature: 0.15,
    geminiThinking: 'low',
  },
  // One notch more reasoning than PRODUCTION, for A/B runs
  // (EVAL_PRESETS=PRODUCTION,GEMINI_3_8_FLASH_MEDIUM).
  GEMINI_3_8_FLASH_MEDIUM: {
    modelKey: 'GEMINI_3_8_FLASH',
    limits: { maxTokens: 16000 },
    temperature: 0.15,
    geminiThinking: 'medium',
  },
  GEMINI_3_5_FLASH_LITE: {
    modelKey: 'GEMINI_3_5_FLASH_LITE',
    limits: { maxTokens: 8000 },
    temperature: 0.15,
    geminiThinking: 'minimal',
  },
  GEMINI_FLASH: {
    modelKey: 'GEMINI_3_FLASH',
    limits: { maxTokens: 40000 },
    temperature: 0.15,
  },
  GEMINI_FLASH_LITE: {
    modelKey: 'GEMINI_3_1_FLASH_LITE',
    limits: { maxTokens: 30000 },
    temperature: 0.2,
  },
  GPT_5_5: {
    modelKey: 'GPT_5_5',
    limits: { maxTokens: 10000 },
    temperature: 0.15,
  },
  GPT_5_4: {
    modelKey: 'GPT_5_4',
    limits: { maxTokens: 10000 },
    temperature: 0.15,
  },
  GPT_5: {
    modelKey: 'GPT_5',
    limits: { maxTokens: 8000 },
    temperature: 0.15,
  },
  DEVELOPMENT: {
    modelKey: 'CLAUDE_HAIKU_4_5',
    limits: { maxTokens: 8000 },
    temperature: 0.2,
  },
  TEST: {
    modelKey: 'CLAUDE_HAIKU_4_5',
    limits: {
      maxTokens: 2000,
      searchLimit: 10,
      processingLimit: 10,
      maxDocumentSelection: 15,
    },
    temperature: 0.2,
  },
} as const satisfies Record<string, RecommendationPreset>

export type PresetName = keyof typeof RECOMMENDATION_PRESETS

/** Build a full RecommendationConfig from a preset name. */
export function buildRecommendationConfig(
  presetName: PresetName
): RecommendationConfig {
  // Widen from the `as const` literal union so optional fields like
  // geminiThinking (absent from most presets) are visible on the type.
  const preset: RecommendationPreset = RECOMMENDATION_PRESETS[presetName]
  const model = MODELS[preset.modelKey]
  return {
    model: { type: model.provider, model: model.id },
    limits: { ...DEFAULT_LIMITS, ...preset.limits },
    parameters: {
      temperature: preset.temperature,
      ...(preset.geminiThinking
        ? { geminiThinking: preset.geminiThinking }
        : {}),
    },
    paths: { ...DEFAULT_PATHS },
    defaults: { ...DEFAULT_DEFAULTS },
  }
}

/** Default production configuration (kept for backwards-compatible imports). */
export const DEFAULT_RECOMMENDATION_CONFIG: RecommendationConfig =
  buildRecommendationConfig('PRODUCTION')
